import React from 'react';

import { createTypedEnum } from '@reckon-web/formschema-enums';

import {
  GetMemberListItem,
  useGetMembersList,
} from '../data/useGetMembersList';
import { useGetClientsByIds } from '../data/useGetClientsById';
import {
  GetJobSignOffListJobResult,
  useGetJobSignOffsList,
} from '../data/useGetJobSignOffsList';
import type { GetClientsByIdsItem } from '../data/useGetClientsById';

const JobSignOffStatusEnum = createTypedEnum({
  None: 'None',
  Requested: 'Requested',
  Approved: 'Approved',
  Revoked: 'Revoked',
  Declined: 'Declined',
  Withdrawn: 'Withdrawn',
} as const);

const SignOffListTypeEnum = createTypedEnum({
  Requested: 'Requested',
  Actioned: 'Actioned',
});

export const SignOffListType = SignOffListTypeEnum.valuesEnum;

export const JobSignOffStatus = JobSignOffStatusEnum.valuesEnum;

export type JobSignOffStatusType = (typeof JobSignOffStatusEnum.values)[number];

export type JobSignOffs = GetJobSignOffListJobResult & {
  statusSortingOrder: number;
  signOffStatus: JobSignOffStatusType;
  debtor: GetClientsByIdsItem['name'];
  user: GetMemberListItem | null;
};

type SignOffs = {
  requestedJobSignOffs: JobSignOffs[];
  actionedJobSignOffs: JobSignOffs[];
};

const signOffStatusSortingOrder: Record<JobSignOffStatusType, number> = {
  [JobSignOffStatus.None]: 1,
  [JobSignOffStatus.Requested]: 2,
  [JobSignOffStatus.Approved]: 3,
  [JobSignOffStatus.Declined]: 4,
  [JobSignOffStatus.Revoked]: 5,
  [JobSignOffStatus.Withdrawn]: 6,
};

export const useQuerySignOffs = ({
  skip,
}: {
  skip: boolean;
}): {
  loading?: boolean;
  error?: boolean;
  data?: SignOffs;
} => {
  const members = useGetMembersList();

  const requestedJobSignOffsList = useGetJobSignOffsList({
    variables: { parameters: { listType: SignOffListType.Requested } },
    skip,
  });

  const actionedJobSignOffsList = useGetJobSignOffsList({
    variables: { parameters: { listType: SignOffListType.Actioned } },
    skip,
  });

  const requestedJobSignOffClientIds = requestedJobSignOffsList.data?.data
    ?.map(({ clientId }) => clientId)
    .filter(<T>(x: T): x is NonNullable<T> => Boolean(x));
  const actionedJobSignOffClientIds = actionedJobSignOffsList.data?.data
    ?.map(({ clientId }) => clientId)
    .filter(<T>(x: T): x is NonNullable<T> => Boolean(x));

  const clientIds = React.useMemo(
    () =>
      requestedJobSignOffClientIds &&
      actionedJobSignOffClientIds && [
        ...new Set([
          ...requestedJobSignOffClientIds,
          ...actionedJobSignOffClientIds,
        ]),
      ],
    [requestedJobSignOffClientIds, actionedJobSignOffClientIds]
  );

  const clients = useGetClientsByIds(
    clientIds && clientIds.length > 0
      ? {
          variables: { cloudIds: clientIds },
        }
      : { skip: true }
  );

  return React.useMemo(() => {
    if (skip) {
      return {};
    }
    if (
      members.loading ||
      (clientIds && clientIds.length > 0 && clients.loading) ||
      requestedJobSignOffsList.loading ||
      actionedJobSignOffsList.loading
    )
      return { loading: true };

    if (
      members.error ||
      clients.error ||
      requestedJobSignOffsList.error ||
      actionedJobSignOffsList.error ||
      !members.data ||
      (clientIds && clientIds.length > 0 && !clients.data) ||
      !requestedJobSignOffsList.data?.data ||
      !actionedJobSignOffsList.data?.data
    ) {
      return { error: true };
    }

    const requestedJobSignOffs = createSignOffsData(
      members.data,
      clients.data || [],
      requestedJobSignOffsList.data?.data
    );

    const actionedJobSignOffs = createSignOffsData(
      members.data,
      clients.data || [],
      actionedJobSignOffsList.data.data
    );

    const data: SignOffs = {
      requestedJobSignOffs,
      actionedJobSignOffs,
    };

    return { data };
  }, [
    members,
    clients,
    requestedJobSignOffsList,
    actionedJobSignOffsList,
    skip,
    clientIds,
  ]);
};

const createSignOffsData = (
  members: GetMemberListItem[],
  clients: GetClientsByIdsItem[],
  JobSignOffsList: GetJobSignOffListJobResult[] | undefined
): JobSignOffs[] => {
  if (!JobSignOffsList) {
    return [];
  }

  return JobSignOffsList?.map((jobSignOff) => {
    const user = members?.find(
      (user) => user.cloudId === jobSignOff.signOffRequestedByUserId
    );
    const client = clients?.find(
      (client) => client.cloudId === jobSignOff.clientId
    );
    // YEP, this is pointless. But prettier/vscode refused to format the line where signOffStatusSortingOrder was used
    const sso = signOffStatusSortingOrder;
    return {
      cloudId: jobSignOff.cloudId,
      name: jobSignOff.name,
      signOffStatus: jobSignOff.signOffStatus ?? JobSignOffStatus.None,
      statusSortingOrder:
        sso[jobSignOff.signOffStatus ?? JobSignOffStatus.None],
      debtor: client?.name,
      user: user ?? null,
      signOffRequestedDateUtc: jobSignOff.signOffRequestedDateUtc,
      signOffActionedDateUtc: jobSignOff.signOffActionedDateUtc,
    };
  }) as JobSignOffs[];
};
